import { computed, observable, action } from 'mobx';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import { IServiceItemBlockStyles } from '~/types/block-styles.types';
import {
    IImage,
    IService,
    ServiceItemType,
    TCropRatio
} from '~/types/model.types';
import { IServiceItemBlockResponse, ITaxResponse } from '~/types/server.types';
import { ContainerableBlock } from '~/models/blocks/containerable-block.model';
import type { RootStore } from '~/stores/root.store';
import { INITIAL_SERVICE_ITEM_VALUES } from '~/components/Builder/Block/ServicesBlock/service-block.utils';
import { DnDTypes, ServiceItemScope } from '~/types/builder.types';
import type { FlowRevision } from '~/models/flow-revision.model';
import { ServiceSelectionItemBlock } from '~/models/blocks/service-selection-item-block.model';
import {
    getServiceItemOverridableProps,
    getStylesAndTaxInfo
} from '~/components/common/InvoiceBlock/invoice.utils';
import { isValidUrl } from '~/utils/url.utils';

export type ServiceItemBlockDataForServer = ReturnType<
    ContainerableBlock['getDataForServer']
> &
    Pick<
        IServiceItemBlockResponse,
        | 'service_item'
        | 'service_type'
        | 'service_image'
        | 'service_asset'
        | 'service_asset_enabled'
    >;

export abstract class ServiceItemBlock extends ContainerableBlock {
    type: 'invoice_service_item_block' | 'service_selection_item_block';
    @observable service_type: ServiceItemType;
    @observable service_item: IService;
    @observable service_image: IImage;
    @observable styles: IServiceItemBlockStyles;
    @observable service_asset: string;
    @observable service_asset_enabled: boolean;

    constructor(
        block: IServiceItemBlockResponse,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
        this.service_item = {
            ...INITIAL_SERVICE_ITEM_VALUES,
            ...this.service_item
        };
        this.service_asset = block.service_asset;
        this.service_asset_enabled = block.service_asset_enabled;
    }

    get childBlocks() {
        return super.childBlocks as ServiceItemBlock[];
    }

    @computed
    get dndType() {
        if (this.isPackage) {
            return DnDTypes.Package;
        }

        if (this.isInPackage) {
            return DnDTypes.ServiceItemInPackage;
        }

        return DnDTypes.ServiceItem;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    dndAcceptedTypes(additionalProps?: any) {
        return this.isInPackage
            ? [DnDTypes.ServiceItem, DnDTypes.ServiceItemInPackage]
            : [
                  DnDTypes.Package,
                  DnDTypes.ServiceItem,
                  DnDTypes.ServiceItemInPackage
              ];
    }

    get isDeletable() {
        return true;
    }

    @computed
    get isSubItem() {
        return this.parent instanceof ServiceItemBlock;
    }

    get hasImage() {
        return !!this.service_image?.url;
    }

    @computed
    get invoiceBlock() {
        return this.flow.invoiceContainerBlock;
    }
    @computed
    get qbServiceMappingItemId() {
        return this.service_item.qb_item_id;
    }

    @action
    setQbServiceMappingItemId(value: string) {
        this.service_item.qb_item_id = value;
    }

    @computed
    get isEmptyPackage() {
        return this.isPackage && !this.children.length;
    }

    @computed
    get subItems() {
        return this.childBlocks as ServiceItemBlock[];
    }

    @computed
    get isInPackage(): boolean {
        return (
            this.isService &&
            this.parent instanceof ServiceItemBlock &&
            this.parent.isPackage
        );
    }

    @computed
    get subItemsTotalPrice(): number {
        return this.subItems.reduce((total, subItem) => {
            return total + subItem.totalPrice;
        }, 0);
    }

    @computed
    get totalPriceIncludingSubItems() {
        return this.totalPrice + this.subItemsTotalPrice;
    }

    @computed
    get isPackage() {
        return this.service_type === ServiceItemType.Package;
    }

    @computed
    get isService() {
        return this.service_type === ServiceItemType.Service;
    }

    @computed
    get numberOfActiveAndVisibleTaxes() {
        const { taxes } = getStylesAndTaxInfo(this.invoiceBlock);
        const sum =
            +(this.service_item.tax_1 && taxes.tax_1?.active) +
            +(this.service_item.tax_2 && taxes.tax_2?.active) +
            +(this.service_item.tax_3 && taxes.tax_3?.active);
        return isNaN(sum) ? 0 : sum;
    }

    @computed
    get isAnyPackageServiceSelected() {
        if (this.isPackage || this.isInPackage) {
            const { selectedBlockId } = this.rootStore.builderUiStore;
            const pkg = this.isPackage ? this : this.parent;
            return (
                pkg.block_id === selectedBlockId ||
                pkg.childBlocks.some(
                    childBlock => childBlock.block_id === selectedBlockId
                )
            );
        }
        return false;
    }

    @computed
    get shouldDisplayCornerRadiusSlider() {
        return true;
    }

    compareOverridableProps(
        newServiceItem: Partial<IService>,
        oldServiceItem: Partial<IService>
    ) {
        const newProps = getServiceItemOverridableProps(newServiceItem);
        const oldProps = getServiceItemOverridableProps(oldServiceItem);

        return !isEqual(newProps, oldProps);
    }

    isDigitalProductLinkAnEmptyString() {
        return this.getTrimmedDigitalProductLink() === '';
    }

    isDigitalProductLinkInvalid(): boolean {
        return !isValidUrl(this.getTrimmedDigitalProductLink());
    }

    @computed
    get errors() {
        const {
            builderUiStore: { showErrors }
        } = this.rootStore;

        if (!showErrors) {
            return {};
        }
        const errors: any = {};

        if (this.service_asset_enabled) {
            if (
                this.service_asset == null ||
                this.isDigitalProductLinkAnEmptyString()
            ) {
                errors.emptyDigitalProductLink = true;
            } else if (this.isDigitalProductLinkInvalid()) {
                errors.invalidDigitalProductLink = true;
            }
        }

        if (
            !this.service_item.title ||
            (this instanceof ServiceSelectionItemBlock &&
                this.invoice_service_item &&
                !this.invoice_service_item.title)
        ) {
            errors.hasErrors = true;
        }

        return errors;
    }

    getHasTitleError(scope: ServiceItemScope) {
        if (this instanceof ServiceSelectionItemBlock) {
            const errors = this.getErrors({ scope });
            return errors.itemTitle || errors.invoiceTitle;
        }
        return this.errors?.hasErrors;
    }

    getDataForInvoice() {
        return this.service_item;
    }

    @computed
    get taxes() {
        return {
            hasTax1: !!this.service_item?.tax_1,
            hasTax2: !!this.service_item?.tax_2,
            hasTax3: !!this.service_item?.tax_3
        };
    }

    @computed
    get taxValuesMap(): Partial<ITaxResponse & { hidden: boolean }>[] {
        return [1, 2, 3]
            .filter(
                num =>
                    !isNil(
                        this.invoiceBlock.invoiceSummaryBlock.invoice_summary[
                            `tax_${num}`
                        ]
                    )
            )
            .map(id => ({
                tax_id: `tax_${id}`,
                active: this.service_item[`tax_${id}`],
                label: this.invoiceBlock.invoiceSummaryBlock.invoice_summary[
                    `tax_${id}`
                ].label,
                hidden: !this.invoiceBlock.invoiceColumnHeaderBlock.styles[
                    `show_tax_${id}`
                ]
            }));
    }

    @computed
    get selectedQuantity(): number {
        return isNil(this.service_item.quantity)
            ? 1
            : this.service_item.quantity;
    }

    @computed
    get totalPrice() {
        const { price_per_unit, quantity } = this.service_item;
        const parsedValue = parseFloat(String(quantity));
        return (Number.isNaN(parsedValue) ? 1 : parsedValue) * price_per_unit;
    }

    @computed
    get allowedCropRatios(): { default: TCropRatio; types: TCropRatio[] } {
        return {
            default: '1:1',
            types: ['1:1']
        };
    }

    private getTrimmedDigitalProductLink() {
        return this.service_asset?.trim();
    }

    getDataForServer(): ServiceItemBlockDataForServer {
        return {
            ...super.getDataForServer(),
            service_item: { ...this.service_item },
            service_type: this.service_type,
            service_image: this.service_image
                ? { ...this.service_image }
                : null,
            service_asset: this.service_asset,
            service_asset_enabled: this.service_asset_enabled
        };
    }
}
